import { Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import { Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { NotificationsApiService, NotificationItem } from '../../../core/services/notifications-api.service';
import { PushNotificationsService, ForegroundNotification } from '../../../core/services/push-notifications.service';

/**
 * NotificationsBellComponent — bell icon + unread badge + dropdown panel.
 *
 * Sources of unread-count updates:
 *   1. Initial load on auth.
 *   2. Poll `/API2/notifications/unread-count` every 60s as a safety net
 *      (catches notifications delivered while the page was backgrounded and
 *      the FCM SW couldn't reach the foreground).
 *   3. Live: `PushNotificationsService.onNotification$` emits whenever a
 *      foreground push arrives → instant badge bump + prepend the new row.
 *
 * Lists the 20 most recent notifications. Clicking an item navigates via
 * its `deep_link` (hash-routed), marks it read, and decrements the badge.
 */
@Component({
    selector: 'app-notifications-bell',
    standalone: true,
    imports: [CommonModule, LucideIconsModule],
    animations: [
        trigger('openClose', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-8px) scale(0.96)' }),
                animate('180ms cubic-bezier(0.16, 1, 0.3, 1)', style({ opacity: 1, transform: 'translateY(0) scale(1)' }))
            ]),
            transition(':leave', [
                animate('140ms cubic-bezier(0.4, 0, 1, 1)', style({ opacity: 0, transform: 'translateY(-4px) scale(0.97)' }))
            ])
        ]),
        trigger('fadeBadge', [
            transition(':enter', [
                style({ opacity: 0, transform: 'scale(0)' }),
                animate('220ms cubic-bezier(0.34, 1.56, 0.64, 1)', style({ opacity: 1, transform: 'scale(1)' }))
            ])
        ])
    ],
    template: `
        <div class="relative">
            <button
                type="button"
                (click)="toggle($event)"
                class="relative inline-flex items-center justify-center rounded-md p-2.5 transition-colors text-[color:var(--muted-foreground)] hover:bg-[color:var(--hover)]"
                aria-label="Notifications"
            >
                <lucide-icon name="bell" class="h-5 w-5 pointer-events-none"></lucide-icon>
                <span
                    *ngIf="unread > 0"
                    [@fadeBadge]
                    class="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold leading-none rounded-full text-white bg-red-500 shadow"
                >
                    {{ unread > 9 ? '9+' : unread }}
                </span>
            </button>

            <!-- Full-screen backdrop: catches outside taps to close on mobile + desktop -->
            <div
                *ngIf="open"
                [@openClose]
                class="fixed inset-0 z-[100]"
                (click)="close()"
            ></div>

            <div
                *ngIf="open"
                [@openClose]
                class="fixed left-2 right-2 top-14 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 z-[2147483647] rounded-xl border shadow-2xl bg-[color:var(--popover)] border-[color:var(--border)] overflow-hidden origin-top-right"
            >
                <div class="flex items-center justify-between px-4 py-3 border-b border-[color:var(--border)]">
                    <span class="text-sm font-semibold text-[color:var(--foreground)]">Notifications</span>
                    <button
                        *ngIf="notifications.length > 0"
                        type="button"
                        (click)="markAllRead()"
                        class="text-xs text-[color:var(--primary)] hover:underline"
                    >
                        Mark all read
                    </button>
                </div>

                <div class="max-h-[60vh] sm:max-h-96 overflow-y-auto">
                    <div *ngIf="notifications.length === 0" class="px-4 py-8 text-center text-sm text-[color:var(--muted-foreground)]">
                        No notifications yet
                    </div>

                    <button
                        *ngFor="let n of notifications"
                        type="button"
                        (click)="onItemClick(n)"
                        class="w-full text-left px-4 py-3 flex gap-3 transition-colors border-b border-[color:var(--border)] last:border-0 hover:bg-[color:var(--hover)]"
                        [style.background]="!n.read_at ? 'rgba(99, 102, 241, 0.06)' : 'transparent'"
                    >
                        <div class="flex-shrink-0 mt-0.5">
                            <div class="h-8 w-8 rounded-full flex items-center justify-center"
                                 [class.bg-[color:var(--primary)]]="!n.read_at"
                                 [class.bg-[color:var(--hover)]]="n.read_at">
                                <lucide-icon
                                    [name]="n.icon || 'bell'"
                                    class="h-3.5 w-3.5"
                                    [class.text-[color:var(--primary-foreground)]]="!n.read_at"
                                    [class.text-[color:var(--muted-foreground)]]="n.read_at"
                                ></lucide-icon>
                            </div>
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="text-sm font-medium text-[color:var(--foreground)] truncate">{{ n.title || 'Notification' }}</p>
                            <p *ngIf="n.body" class="text-xs text-[color:var(--muted-foreground)] mt-0.5 line-clamp-2">{{ n.body }}</p>
                            <p *ngIf="n.created_at" class="text-[10px] text-[color:var(--text-subtle)] mt-1">{{ formatTime(n.created_at) }}</p>
                        </div>
                    </button>
                </div>

                <div class="px-4 py-2 border-t border-[color:var(--border)]">
                    <button
                        type="button"
                        (click)="showAll()"
                        class="w-full text-center text-xs font-semibold text-[color:var(--primary)] hover:underline"
                    >
                        Show all
                    </button>
                </div>
            </div>
        </div>
    `
})
export class NotificationsBellComponent implements OnInit, OnDestroy {
    private api = inject(NotificationsApiService);
    private push = inject(PushNotificationsService);
    private router = inject(Router);
    private destroy$ = new Subject<void>();

    open = false;
    unread = 0;
    notifications: NotificationItem[] = [];

    ngOnInit(): void {
        this.refreshUnread();
        // 60s poll as a safety net for backgrounded-page gaps.
        interval(60_000)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.refreshUnread());

        // Live: each foreground push bumps the badge + prepends the new row
        // (so the user sees it immediately without re-opening the panel).
        this.push.onNotification$.pipe(takeUntil(this.destroy$)).subscribe((fg: ForegroundNotification) => {
            this.unread += 1;
            // Prepend a synthetic row so the open panel reflects the push.
            const synthetic: NotificationItem = {
                id: 'pending-' + Date.now(),
                org_id: null,
                user_id: '',
                type: fg.data?.['type'] || 'system_announcement',
                title: fg.title,
                body: fg.body,
                deep_link: fg.deepLink,
                data: fg.data as any,
                actor_user_id: null,
                priority: 'normal',
                expires_at: null,
                icon: fg.data?.['icon'] || null,
                read_at: null,
                push_status: null,
                push_attempts: null,
                last_push_error: null,
                pushed_at: fg.receivedAt,
                created_at: fg.receivedAt,
                updated_at: fg.receivedAt
            };
            this.notifications = [synthetic, ...this.notifications].slice(0, 20);
        });
    }

    private refreshUnread(): void {
        this.api.unreadCount().pipe(takeUntil(this.destroy$)).subscribe({
            next: (count) => (this.unread = count),
            error: () => {}
        });
    }

    private loadList(): void {
        this.api.list({ limit: 20 }).pipe(takeUntil(this.destroy$)).subscribe({
            next: (resp) => (this.notifications = resp.items),
            error: () => {}
        });
    }

    toggle(event: Event): void {
        event.stopPropagation();
        this.open = !this.open;
        if (this.open) {
            this.loadList();
        }
    }

    close(): void {
        this.open = false;
    }

    showAll(): void {
        this.open = false;
        this.router.navigateByUrl('/developer/notifications');
    }

    onItemClick(n: NotificationItem): void {
        if (!n.read_at && !n.id.startsWith('pending-')) {
            this.api.markRead(n.id).pipe(takeUntil(this.destroy$)).subscribe({
                next: () => {
                    n.read_at = new Date().toISOString();
                    this.unread = Math.max(0, this.unread - 1);
                },
                error: () => {}
            });
        } else if (n.id.startsWith('pending-')) {
            // Synthetic row from a live push — re-fetch the real list so the
            // row gets a real id + the mark-read call has a target.
            this.loadList();
        }
        if (n.deep_link) {
            this.open = false;
            // Shared routing: normalizes every deep-link shape (the bell used
            // to mishandle the absolute apex URLs the catalog now emits) and
            // applies the type-level overrides (pending offer approvals →
            // the approvals page, new leads → Lead Management).
            this.push.handleDeepLink(n.deep_link, n.type);
        }
    }

    markAllRead(): void {
        this.api.markAllRead().pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.notifications.forEach((n) => {
                    if (!n.read_at) n.read_at = new Date().toISOString();
                });
                this.unread = 0;
            },
            error: () => {}
        });
    }

    formatTime(iso: string): string {
        try {
            const d = new Date(iso);
            const now = new Date();
            const diff = now.getTime() - d.getTime();
            const mins = Math.floor(diff / 60000);
            if (mins < 1) return 'just now';
            if (mins < 60) return mins + 'm ago';
            const hrs = Math.floor(mins / 60);
            if (hrs < 24) return hrs + 'h ago';
            const days = Math.floor(hrs / 24);
            if (days < 7) return days + 'd ago';
            return d.toLocaleDateString();
        } catch {
            return '';
        }
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.open) this.open = false;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
